import { Col, Row, Progress } from 'antd';
import { Title, Body, Colors } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled from 'styled-components/macro';
import { getValidInstancesPercentage } from 'utils/data-validation';
import {
  getFormatedDifference,
  getDiffArrowDirection,
  getResultColorFromPercentage,
  getResultColorVariantFromValue,
} from 'utils/runStatistics';
import { RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { DownloadResultsAsJsonButton } from 'components/DownloadResultsAsJsonButton';

export const RunStatistics = ({
  selectedRun,
  pastRun,
}: {
  selectedRun?: RuleRunStatus | null;
  pastRun?: RuleRunStatus | null;
}) => {
  const currentTotalValid = selectedRun?.totalValid || 0;
  const currentTotalFailed = selectedRun?.totalFailed || 0;
  const currentFiltered = currentTotalValid + currentTotalFailed;

  const pastTotalValid = pastRun?.totalValid || 0;
  const pastTotalFailed = pastRun?.totalFailed || 0;
  const pastFiltered = pastTotalValid + pastTotalFailed;

  const selectedRunPercentage = getValidInstancesPercentage(
    currentTotalValid,
    currentTotalFailed
  );

  const renderLabelIcon = (difference: number) => (
    <>
      {difference === 0 && <>+</>}
      {getFormatedDifference(difference)}
      {difference !== 0 && <Icon type={getDiffArrowDirection(difference)} />}
    </>
  );

  const renderDifferenceFilteredInstancesLabel = () => {
    const filteredInstancesDifference = currentFiltered - pastFiltered;
    return (
      <StyledLabel>{renderLabelIcon(filteredInstancesDifference)}</StyledLabel>
    );
  };

  const renderDifferenceFailedInstancesLabel = () => {
    const failedInstancesDifference = currentTotalFailed - pastTotalFailed;
    const resultColorVariant = getResultColorVariantFromValue(
      -failedInstancesDifference
    );
    return (
      <StyledLabel>{renderLabelIcon(failedInstancesDifference)}</StyledLabel>
    );
  };

  const renderRunResult = () => {
    const resultColor = getResultColorFromPercentage(selectedRunPercentage);
    return (
      <StyledResultContainer align="middle">
        <Progress
          width={80}
          strokeWidth={14}
          strokeColor={resultColor}
          strokeLinecap="square"
          type="circle"
          percent={selectedRunPercentage}
          format={(percent) =>
            percent && (
              <>
                <b>{`${percent.toPrecision(3)}%`}</b> valid
              </>
            )
          }
        />
        <Col>
          <StyledRow align="middle">
            <Body level={6}>Filtered instances checked</Body>
            <Title level={6}>{currentFiltered}</Title>
            {renderDifferenceFilteredInstancesLabel()}
          </StyledRow>
          <StyledRow align="middle">
            <Body level={6}>Failed instances</Body>
            <Title level={6}>{selectedRun?.totalFailed}</Title>
            {renderDifferenceFailedInstancesLabel()}
          </StyledRow>
        </Col>
        <StyledDownloadButton>
          <DownloadResultsAsJsonButton
            runId={selectedRun?.externalId}
            buttonText="Download .json"
            disabled={selectedRun?.totalFailed === 0}
            fileName={
              selectedRun?.rule?.name
                ? `${selectedRun.rule.name}_failed-instances.json`
                : 'failed-instances'
            }
          />
        </StyledDownloadButton>
      </StyledResultContainer>
    );
  };

  return renderRunResult();
};

const StyledRow = styled(Row)`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const StyledResultContainer = styled(StyledRow)`
  margin: 1rem;
  gap: 1rem;

  .ant-progress-text {
    max-width: 50px;
    font-size: 14px;
  }

  .cogs-title-6 {
    font-weight: 600;
  }
`;

const StyledDownloadButton = styled.div`
  padding-left: 95px;
`;

const StyledLabel = styled.div`
  display: inline-flex;
  padding: 0 5px;
  background: ${Colors['decorative--blue--100']};
  color: ${Colors['decorative--blue--500']};
  font-weight: 600;
  border-radius: 6px;
  gap: 0.2rem;
`;
